import { ChangeDetectionStrategy, Component, computed, inject, Injectable } from '@angular/core';
import { LanguageService } from '@core/i18n/language.service';
import { injectToastContext, NgpToast, NgpToastManager } from 'ng-primitives/toast';
import { EaseIcon } from '../icon/icon';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info';

/**
 * Toast placement options.
 * - 'top-start' = top-left (in LTR)
 * - 'top-end' = top-right (in LTR)
 * - 'top-center' = top-center
 * - 'bottom-start' = bottom-left (in LTR)
 * - 'bottom-end' = bottom-right (in LTR)
 * - 'bottom-center' = bottom-center
 */
export type ToastPlacement = 'top-start' | 'top-end' | 'top-center' | 'bottom-start' | 'bottom-end' | 'bottom-center';

export interface ToastData {
  title: string;
  description?: string;
  variant?: ToastVariant;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Toast component - displays a single toast notification
 * Uses ng-primitives NgpToast as hostDirective for positioning, stacking, and swipe-to-dismiss
 */
@Component({
  selector: 'ease-toast',
  standalone: true,
  imports: [EaseIcon],
  hostDirectives: [NgpToast],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [class]="getToastClasses()" role="alert" aria-live="polite">
      <div class="tw-flex tw-items-start tw-gap-3">
        @switch (context.variant ?? 'default') { @case ('success') {
        <ease-icon
          name="CircleCheck"
          class="tw-text-success-bold dark:tw-text-success-contrast tw-mt-0.5 tw-shrink-0"
        />
        } @case ('error') {
        <ease-icon
          name="CircleX"
          class="tw-text-destructive-bold dark:tw-text-destructive-contrast tw-mt-0.5 tw-shrink-0"
        />
        } @case ('warning') {
        <ease-icon
          name="CircleAlert"
          class="tw-text-warning-bold dark:tw-text-warning-contrast tw-mt-0.5 tw-shrink-0"
        />
        } @case ('info') {
        <ease-icon name="Info" class="tw-text-info-bold dark:tw-text-info-contrast tw-mt-0.5 tw-shrink-0" />
        } }

        <div class="tw-flex-1 tw-min-w-0">
          <p class="toast-title tw-font-medium tw-text-sm tw-text-charcoal dark:tw-text-white">
            {{ context.title }}
          </p>
          @if (context.description) {
          <p class="toast-description tw-text-sm tw-text-muted dark:tw-text-muted-inverted tw-mt-1">
            {{ context.description }}
          </p>
          } @if (context.action) {
          <button
            (click)="handleAction()"
            class="tw-mt-2 tw-text-sm tw-font-medium tw-text-forest hover:tw-underline tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer dark:tw-text-sage"
          >
            {{ context.action.label }}
          </button>
          }
        </div>

        <button
          (click)="dismiss()"
          class="tw-rounded-sm tw-opacity-70 hover:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 dark:focus:tw-ring-inverted tw-bg-transparent tw-border-none tw-p-1 tw-cursor-pointer tw-shrink-0"
        >
          <ease-icon name="X" size="sm" />
          <span class="tw-sr-only">{{ dismissLabel() }}</span>
        </button>
      </div>
    </div>
  `,
  styles: `
    :host {
      position: absolute;
      touch-action: none;
      box-sizing: border-box;
      display: block;
      width: 350px;
      opacity: 0;
      height: fit-content;
      transform: var(--y);
      transition: all 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
    }

    :host[data-position-x='end'] {
      right: 0;
    }

    :host[data-position-x='start'] {
      left: 0;
    }

    :host[data-position-x='center'] {
      left: 50%;
      translate: -50% 0;
    }

    :host[data-position-y='top'] {
      top: 0;
      --lift: 1;
      --lift-amount: calc(var(--lift) * var(--ngp-toast-gap));
      --y: translateY(-100%);
    }

    :host[data-position-y='bottom'] {
      bottom: 0;
      --lift: -1;
      --lift-amount: calc(var(--lift) * var(--ngp-toast-gap));
      --y: translateY(100%);
    }

    :host[data-enter] {
      opacity: 1;
      --y: translateY(0);
    }

    :host[data-exit] {
      opacity: 0;
      --y: translateY(calc(calc(var(--lift) * var(--ngp-toast-gap)) * -1));
    }

    :host[data-visible='false'] {
      opacity: 0;
      pointer-events: none;
    }

    :host[data-expanded='true']::after {
      content: '';
      position: absolute;
      left: 0;
      height: calc(var(--ngp-toast-gap) + 1px);
      bottom: 100%;
      width: 100%;
    }

    :host[data-expanded='false'][data-front='false'] {
      --scale: var(--ngp-toasts-before) * 0.05 + 1;
      --y: translateY(calc(var(--lift-amount) * var(--ngp-toasts-before)))
        scale(calc(-1 * var(--scale)));
      height: var(--ngp-toast-front-height);
    }

    :host[data-expanded='true'] {
      --y: translateY(calc(var(--lift) * var(--ngp-toast-offset)));
      height: auto;
    }

    :host[data-swiping='true'] {
      transform: var(--y) translateY(var(--ngp-toast-swipe-amount-y, 0))
        translateX(var(--ngp-toast-swipe-amount-x, 0));
      transition: none;
    }

    :host[data-swiping='true'][data-swipe-direction='left'] {
      opacity: calc(1 - clamp(0, ((-1 * var(--ngp-toast-swipe-x, 0px)) - 45) / 55, 1));
    }

    :host[data-swiping='true'][data-swipe-direction='right'] {
      opacity: calc(1 - clamp(0, (var(--ngp-toast-swipe-x, 0px) - 45) / 55, 1));
    }

    :host[data-swiping='true'][data-swipe-direction='top'] {
      opacity: calc(1 - clamp(0, ((-1 * var(--ngp-toast-swipe-y, 0px)) - 45) / 55, 1));
    }

    :host[data-swiping='true'][data-swipe-direction='bottom'] {
      opacity: calc(1 - clamp(0, (var(--ngp-toast-swipe-y, 0px) - 45) / 55, 1));
    }

    /* Truncate text when toast is collapsed behind front */
    :host[data-front='false'][data-expanded='false'] .toast-title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    :host[data-front='false'][data-expanded='false'] .toast-description {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Bottom position animations */
    :host[data-position-y='bottom'].toast-enter {
      animation: toast-slide-in-bottom 400ms cubic-bezier(0.215, 0.61, 0.355, 1);
    }

    :host[data-position-y='bottom'].toast-leave {
      opacity: 0;
      transform: translateY(100%);
      transition:
        opacity 400ms cubic-bezier(0.215, 0.61, 0.355, 1),
        transform 400ms cubic-bezier(0.215, 0.61, 0.355, 1);
    }

    /* Top position animations */
    :host[data-position-y='top'].toast-enter {
      animation: toast-slide-in-top 400ms cubic-bezier(0.215, 0.61, 0.355, 1);
    }

    :host[data-position-y='top'].toast-leave {
      opacity: 0;
      transform: translateY(-100%);
      transition:
        opacity 400ms cubic-bezier(0.215, 0.61, 0.355, 1),
        transform 400ms cubic-bezier(0.215, 0.61, 0.355, 1);
    }

    @keyframes toast-slide-in-bottom {
      from {
        opacity: 0;
        transform: translateY(100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes toast-slide-in-top {
      from {
        opacity: 0;
        transform: translateY(-100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
  host: {
    'animate.enter': 'toast-enter',
    'animate.leave': 'toast-leave',
  },
})
export class EaseToast {
  private toastManager = inject(NgpToastManager);
  private ngpToast = inject(NgpToast);
  private languageService = inject(LanguageService);

  context = injectToastContext<ToastData>();

  /** Translated dismiss label for screen readers */
  readonly dismissLabel = computed(() => this.languageService.translate('ui.toast.dismiss'));

  getToastClasses(): string {
    const base = ['tw-p-4', 'tw-rounded-lg', 'tw-shadow-lg', 'tw-pointer-events-auto', 'tw-border', 'tw-border-solid'];

    switch (this.context.variant) {
      case 'success':
        base.push(
          'tw-bg-success-subtle dark:tw-bg-success-bold tw-border-success-bold dark:tw-border-success-contrast'
        );
        break;
      case 'error':
        base.push(
          'tw-bg-destructive-subtle dark:tw-bg-destructive-bold tw-border-destructive-bold dark:tw-border-destructive-contrast'
        );
        break;
      case 'warning':
        base.push(
          'tw-bg-warning-subtle dark:tw-bg-warning-bold tw-border-warning-bold dark:tw-border-warning-contrast'
        );
        break;
      case 'info':
        base.push('tw-bg-info-subtle dark:tw-bg-info-bold tw-border-info-bold dark:tw-border-info-contrast');
        break;
      default:
        base.push('tw-bg-white dark:tw-bg-forest tw-border-border dark:tw-border-border-bold');
    }

    return base.join(' ');
  }

  handleAction(): void {
    this.context.action?.onClick();
    this.dismiss();
  }

  dismiss(): void {
    this.toastManager.dismiss(this.ngpToast);
  }
}

export interface ToastOptions {
  duration?: number;
  /** If true, toast won't auto-dismiss and must be closed manually */
  persistent?: boolean;
  /** Where to position the toast. Defaults to the global config placement (bottom-end). */
  placement?: ToastPlacement;
}

/**
 * Toast service - provides convenience methods for showing toasts
 * Wraps NgpToastManager with typed helper methods
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private toastManager = inject(NgpToastManager);

  show(data: ToastData, options?: ToastOptions): { dismiss: () => Promise<void> } {
    // Use a very large duration for persistent toasts (Infinity doesn't work with setTimeout)
    const PERSISTENT_DURATION = 24 * 60 * 60 * 1000; // 24 hours in ms
    return this.toastManager.show(EaseToast, {
      context: data,
      duration: options?.persistent ? PERSISTENT_DURATION : options?.duration,
      placement: options?.placement,
    });
  }

  success(title: string, description?: string, options?: ToastOptions): { dismiss: () => Promise<void> } {
    return this.show({ title, description, variant: 'success' }, options);
  }

  error(title: string, description?: string, options?: ToastOptions): { dismiss: () => Promise<void> } {
    return this.show({ title, description, variant: 'error' }, options);
  }

  warning(title: string, description?: string, options?: ToastOptions): { dismiss: () => Promise<void> } {
    return this.show({ title, description, variant: 'warning' }, options);
  }

  info(title: string, description?: string, options?: ToastOptions): { dismiss: () => Promise<void> } {
    return this.show({ title, description, variant: 'info' }, options);
  }
}

// Re-export for convenience
export { provideToastConfig } from 'ng-primitives/toast';
